/**
 * Vector utility functions for RAG implementation
 */

/**
 * Calculate cosine similarity between two vectors
 * Returns a value between -1 and 1, where 1 means identical
 */
export function cosineSimilarity(vecA: number[], vecB: number[]): number {
  if (vecA.length !== vecB.length) {
    throw new Error('Vectors must have the same length');
  }

  let dotProduct = 0;
  let normA = 0;
  let normB = 0;

  for (let i = 0; i < vecA.length; i++) {
    dotProduct += vecA[i] * vecB[i];
    normA += vecA[i] * vecA[i];
    normB += vecB[i] * vecB[i];
  }

  const denominator = Math.sqrt(normA) * Math.sqrt(normB);
  if (denominator === 0) return 0;

  return dotProduct / denominator;
}

/**
 * Estimate token count for text using a simple heuristic
 * Roughly 4 characters per token for English text
 */
export function estimateTokenCount(text: string): number {
  // Remove extra whitespace
  const cleanText = text.trim().replace(/\s+/g, ' ');
  
  // Rough estimate: 1 token ≈ 4 characters for English
  // This is a conservative estimate
  return Math.ceil(cleanText.length / 4);
}

/**
 * Format a chunk with its context for display in the AI prompt
 */
export interface PolicyChunk {
  policy_id: string;
  policy_title?: string;
  chunk_heading: string;
  chunk_content: string;
  similarity_score?: number;
  chunk_index?: number;
}

export function formatChunkContext(chunk: PolicyChunk): string {
  const { policy_title, chunk_heading, chunk_content, similarity_score } = chunk;
  
  let context = '';
  
  if (policy_title) {
    context += `**Policy**: ${policy_title}\n`;
  }
  
  if (chunk_heading) {
    context += `**Section**: ${chunk_heading}\n`;
  }
  
  if (similarity_score !== undefined) {
    context += `**Relevance**: ${(similarity_score * 100).toFixed(1)}%\n`;
  }
  
  context += `\n${chunk_content}\n`;
  
  return context;
}

/**
 * Format multiple chunks for the RAG context in the system prompt
 */
export function formatRAGContext(chunks: PolicyChunk[]): string {
  if (chunks.length === 0) {
    return 'No relevant approved policy content found.';
  }

  const formattedChunks = chunks
    .map((chunk, index) => {
      return `### Retrieved Context ${index + 1}\n${formatChunkContext(chunk)}`;
    })
    .join('\n\n---\n\n');

  return `The following are the most relevant sections from approved policies based on the user's query:\n\n${formattedChunks}`;
}

/**
 * Create a simple hash for content to detect changes
 */
export async function hashContent(content: string): Promise<string> {
  // Use Web Crypto API for hashing
  const encoder = new TextEncoder();
  const data = encoder.encode(content);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

/**
 * Truncate text to a maximum token count
 */
export function truncateToTokens(text: string, maxTokens: number): string {
  const estimatedTokens = estimateTokenCount(text);
  
  if (estimatedTokens <= maxTokens) {
    return text;
  }
  
  // Calculate approximate character limit
  const maxChars = maxTokens * 4;
  const truncated = text.substring(0, maxChars);
  
  // Try to end at a sentence boundary
  const lastPeriod = truncated.lastIndexOf('.');
  const lastNewline = truncated.lastIndexOf('\n');
  const cutPoint = Math.max(lastPeriod, lastNewline);
  
  if (cutPoint > maxChars * 0.8) {
    return truncated.substring(0, cutPoint + 1);
  }
  
  return truncated + '...';
}

